import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { shape, string } from 'prop-types'
import { Component } from 'react'
import { connectStateResults } from 'react-instantsearch-dom'
import { compose } from 'redux'

import { TENANT_CH } from 'shared/consts'
import { decorateNoveltiesSortingOptions } from 'shared/experiments/components/NoveltiesSortingOptions'
import { isMarketplace } from 'shared/experiments/utils/featureFlags'
import {
  getAverageRatingRangeRefinement,
  getDefaultHitsPerPage,
  getDefaultRange,
  getFiltersOrder,
  getRangeFromResults,
  getSortingItems,
} from 'shared/utils/algolia/filters'
import { getNoveltiesIndexName } from 'shared/utils/algolia/getNoveltiesIndexName'
import { updateGTM } from 'shared/utils/gtmUtils'
import { withI18n } from 'shared/utils/withI18n'
import Button from 'views/components/atoms/Button'
import { currencyToSymbol } from 'views/components/atoms/Price/helpers'
import ModalShowButton from 'views/components/molecules/ModalShowButton'
import CategoryList from 'views/components/organisms/AlgoliaInstantSearch/Algolia/CategoryList'
import ClearRefinements from 'views/components/organisms/AlgoliaInstantSearch/Algolia/ClearRefinements'
import { getHitsPerPageItems } from 'views/components/organisms/AlgoliaInstantSearch/Algolia/helpers'
import HitsPerPage from 'views/components/organisms/AlgoliaInstantSearch/Algolia/HitsPerPage'
import InputRange from 'views/components/organisms/AlgoliaInstantSearch/Algolia/InputRange'
import MultipleSelectionList from 'views/components/organisms/AlgoliaInstantSearch/Algolia/MultipleSelectionList'
import PrescriptionTypesFilter from 'views/components/organisms/AlgoliaInstantSearch/Algolia/PrescriptionTypesFilter'
import SortBy from 'views/components/organisms/AlgoliaInstantSearch/Algolia/SortBy'
import StarRatingSlider from 'views/components/organisms/AlgoliaInstantSearch/Algolia/StarRatingSlider'
import Stats from 'views/components/organisms/AlgoliaInstantSearch/Algolia/Stats'
import Device from 'views/components/utils/Device'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { i18nType, publicRuntimeConfigType } from 'views/utils/propTypes'

import {
  FACETS_LIMIT,
  FILTER_ID,
  PRICE_DEFAULT_VALUES,
  SHIPPING_OPTIONS_FILTER_NAME,
} from './consts'
import DynamicFilters from './DynamicFilters'
import ProductFilterModal from './ProductFilterModal'

import './ProductFilter.scss'

class ProductFilter extends Component {
  state = this.getInitialState()

  getInitialState() {
    const { searchState, searchResults, i18n } = this.props

    const nowProductGroup = {
      id: 'now-refinement-control',
      attribute: 'nowProductGroup',
      label: i18n._('general.label.filterNow'),
      customRefinementGroupName: SHIPPING_OPTIONS_FILTER_NAME,
      nowLabel: true,
      value: 'sameday_nextday',
    }

    const shippingOptionsData = [nowProductGroup]

    return {
      searchState,
      searchResults,
      hitsPerPageItems: getHitsPerPageItems(),
      defaultHitsPerPage: getDefaultHitsPerPage(searchState),
      shippingOptionsData,
      averageRating: getAverageRatingRangeRefinement(searchState),
      filtersOrder: getFiltersOrder(searchResults),
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.searchResults.queryID !== this.props.searchResults.queryID) {
      const { searchState } = this.props

      /** @type {ResultsStateReact} */
      const { searchResults } = this.props

      updateGTM(searchState, searchResults)
    }
  }

  getDefaultValues = facetName => {
    const { searchState, searchResults } = this.props

    if (searchState && searchResults) {
      const range = getDefaultRange(searchState, searchResults, facetName)

      if (range) {
        return range
      }
    }

    return {
      min: PRICE_DEFAULT_VALUES.min,
      max: PRICE_DEFAULT_VALUES.max,
    }
  }

  getRangeValues = facetName => {
    const { searchResults } = this.props
    if (searchResults) {
      const range = getRangeFromResults(searchResults, facetName)

      if (range) {
        return range
      }
    }

    return {
      min: PRICE_DEFAULT_VALUES.min,
      max: PRICE_DEFAULT_VALUES.max,
    }
  }

  isShippingOptionsFilterVisible() {
    const disjunctiveFacets = this.props.searchResults?.disjunctiveFacets || []

    return Boolean(
      disjunctiveFacets.find(({ name }) => name === 'nowProductGroup')?.data
        ?.sameday_nextday
    )
  }

  getRuleContexts() {
    const {
      publicRuntimeConfig: {
        pageProperties: { ruleContexts },
      },
    } = this.props

    return ruleContexts
  }

  render() {
    const {
      i18n,
      indexName,
      searchState: { refinementList, query },
      publicRuntimeConfig: {
        pageProperties: { experiments, ruleContexts },
        publicConfig: { searchConfig, tenant, currency },
      },
    } = this.props

    const {
      averageRating,
      shippingOptionsData,
      hitsPerPageItems,
      defaultHitsPerPage,
      filtersOrder,
    } = this.state

    const noveltiesIndexName = getNoveltiesIndexName(
      isMarketplace(experiments, tenant),
      searchConfig
    )

    const sortingItems = decorateNoveltiesSortingOptions({
      sortingItems: getSortingItems({
        indexName,
        i18n,
        isSearchResultsPage: Boolean(query),
      }),
      ruleContexts,
      label: i18n._('general.label.sort.Novelties'),
      noveltiesIndexName,
    })

    return (
      <div className="o-ProductFilter">
        <p className="u-hidden@small-tiny u-border--bottom u-padding--bottom u-no-margin u-font-weight--bold o-ProductFilter__filter-search-title">
          <Trans id="general.label.filterSearch" />
        </p>
        <div className="u-padding--bottom u-hidden@medium l-flex">
          <ModalShowButton
            name={`modal-open-${FILTER_ID}`}
            modalId={FILTER_ID}
            className="o-ProductFilter__filter-search-button"
          >
            <Trans id="general.label.filterSearch" />
          </ModalShowButton>
        </div>
        <ProductFilterModal
          title={i18n._('general.label.selectFilter')}
          id={FILTER_ID}
        >
          <Device desktop tablet>
            <div className="u-padding@small-tiny u-hidden@small-tiny u-padding--top o-ProductFilter__clear-button-container">
              <ClearRefinements
                filterId={FILTER_ID}
                label={i18n._('general.label.clearFilters')}
              />
            </div>
          </Device>

          <div className="u-hidden@medium">
            <SortBy currentRefinement={indexName} items={sortingItems} mobile />
          </div>

          <div className="u-padding--sides@small-tiny o-ProductFilter__dynamic-filters__wrapper">
            <DynamicFilters order={filtersOrder}>
              <div className="u-hidden@medium">
                <HitsPerPage
                  items={hitsPerPageItems}
                  defaultRefinement={defaultHitsPerPage}
                  label={i18n._('general.label.sort.Anzeigen')}
                  mobile
                />
              </div>
              <MultipleSelectionList
                attribute="pharmaForm"
                label={i18n._('general.label.filter.pharmaceuticalForm')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <div
                className="u-no-border-radius u-border--bottom u-padding@small-tiny u-padding-x-large--bottom@small-tiny u-padding--bottom@medium o-ProductFilter__average-rating__wrapper o-ProductFilter__item"
                data-attribute="averageRating"
              >
                <StarRatingSlider
                  label={i18n._('ratingOverview.headline')}
                  attribute="averageRating"
                  name="productFilter__ratingSlider"
                  delimiterLabel={i18n._('general.label.until')}
                  defaultValues={averageRating}
                />
              </div>
              <div
                className={classNames(
                  'u-no-border-radius u-border--bottom u-padding@small-tiny u-padding-x-large--bottom@small-tiny o-ProductFilter__price__wrapper o-ProductFilter__item',
                  {
                    'o-ProductFilter__item--ch': tenant === TENANT_CH,
                  }
                )}
                data-attribute="price"
              >
                <InputRange
                  label={i18n._('general.label.sort.price')}
                  attribute="price"
                  name="productFilter__rangeInput"
                  rangeValues={this.getRangeValues('price')}
                  defaultValues={this.getDefaultValues('price')}
                  symbol={currencyToSymbol(currency)}
                  delimiterLabel="-"
                />
              </div>
              <MultipleSelectionList
                attribute="packSize"
                label={i18n._('general.label.quantity')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="brandIntern"
                label={i18n._('general.label.brand')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <CategoryList
                attribute="primaryCategory"
                label={i18n._('general.label.categories')}
                limit={FACETS_LIMIT}
                className="o-AlgoliaInstantSearch__ProductFilter--category-wrapper o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="activeSubstances"
                label={i18n._('general.label.filter.substances')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <PrescriptionTypesFilter
                attribute="prescriptionMedicine"
                label={i18n._('prescription.labels.prescriptionType')}
                trueOptionLabel={i18n._(
                  'prescription.labels.prescriptionRequired'
                )}
                falseOptionLabel={i18n._(
                  'prescription.labels.noPrescriptionRequired'
                )}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="manufacturer"
                label={i18n._('general.label.manufacturer')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="filterAttributes"
                label={i18n._('general.label.filter.attributes')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="potency"
                label={i18n._('general.label.potency')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="hair_type_multi"
                label={i18n._('general.label.hairtype')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="skin_type_multi"
                label={i18n._('general.label.skintype')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="uv_protection"
                label={i18n._('general.label.uv_protection')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="lens_types"
                label={i18n._('general.label.lens_types')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="absorbency"
                label={i18n._('general.label.absorbency')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="book_language"
                label={i18n._('general.label.language')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="authors"
                label={i18n._('general.label.author')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="application_areas"
                label={i18n._('general.label.filter.applicationAreas')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />

              <MultipleSelectionList
                attribute="shippingOptions"
                label={i18n._('general.label.shippingOptions')}
                limit={FACETS_LIMIT}
                isHidden={!this.isShippingOptionsFilterVisible()}
                shippingOptionsData={shippingOptionsData}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />

              <MultipleSelectionList
                attribute="animal_species"
                label={i18n._('general.label.animalSpecies')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="voltage"
                label={i18n._('general.label.voltage')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="schuessler_salts"
                label={i18n._('general.label.schuesslerSalts')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="certificates"
                label={i18n._('general.label.filter.certificates')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="recommended_age"
                label={i18n._('general.label.age')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="hair_color"
                label={i18n._('general.label.hairColor')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="hair_length"
                label={i18n._('general.label.hairLength')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="haircut"
                label={i18n._('general.label.haircut')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="head_circumference"
                label={i18n._('general.label.headCircumference')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                attribute="publisher"
                label={i18n._('general.label.publisher')}
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                label={i18n._('general.label.lifeStage')}
                attribute="life_stage"
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                label={i18n._('general.label.variety')}
                attribute="variety"
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                label={i18n._('general.label.specialNeeds')}
                attribute="special_needs"
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                label={i18n._('general.label.filter.divisibility')}
                attribute="divisibility"
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                label={i18n._('general.label.filter.sport')}
                attribute="sports"
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                label={i18n._('general.label.filter.texture')}
                attribute="texture"
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                label={i18n._('general.label.filter.productType')}
                attribute="product_type_filter"
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                label={i18n._('general.label.filter.minerals')}
                attribute="mineral_and_trace_elements"
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
              <MultipleSelectionList
                label={i18n._('general.label.filter.vitamins')}
                attribute="vitamins"
                limit={FACETS_LIMIT}
                searchStateRefinementList={refinementList}
                className="o-ProductFilter__item"
              />
            </DynamicFilters>
          </div>
          <div className="o-ProductFilter__modal-buttons">
            <div className="u-hidden@medium u-padding o-ProductFilter__modal-button-container">
              <Button
                name={`modal-close-${FILTER_ID}`}
                className="o-ProductFilter__cta"
                data-clientside-hook="productFilter__close-modal"
                isClientsideControlled
                ariaHidden="true"
                data-a11y-dialog-hide
              >
                <Trans id="general.link.label.close" />
              </Button>
              <Button
                name={`apply-filter-${FILTER_ID}`}
                className="o-ProductFilter__cta"
                isClientsideControlled
                data-clientside-hook="productFilter__apply-filter"
                ariaHidden="true"
                data-a11y-dialog-hide
              >
                <Stats label={i18n._('general.label.showResults')} />
              </Button>
            </div>
            <br className="u-hidden@small-tiny p-ProductFilter__break-line" />
            <div className="u-padding@small-tiny u-hidden@medium o-ProductFilter__modal-button-container">
              <ClearRefinements
                filterId={FILTER_ID}
                label={i18n._('general.label.clearFilters')}
                isMobile
              />
            </div>
          </div>
        </ProductFilterModal>
      </div>
    )
  }
}

ProductFilter.propTypes = {
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  i18n: i18nType.isRequired,
  indexName: string.isRequired,
  searchState: shape({}),
  searchResults: shape({}),
}

ProductFilter.defaultProps = {
  searchState: null,
  searchResults: {},
}

export default compose(
  connectStateResults,
  withI18n(),
  withShopAndRuntimeConfig
)(ProductFilter)
